import { ReactNode, useEffect, useState, useCallback, lazy, Suspense } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Activity, LogOut, Settings, Pencil, Menu, ChevronDown, Star } from "lucide-react";
import { useCalendarSync } from "@/hooks/use-calendar-sync";
import { useOrganization } from "@/hooks/use-organization";
import { useFeatureFlags } from "@/hooks/use-feature-flags";
import { ProfileEditDialog } from "@/components/ProfileEditDialog";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useSwipe } from "@/hooks/use-swipe";
import { useSidebarNavigation, FAVORITES_SECTION_ID } from "@/hooks/use-sidebar-navigation";
import { SidebarNavigation } from "@/components/nav/SidebarNavigation";
import { DynamicIcon } from "@/components/nav/DynamicIcon";
import { AIAgentChat } from "@/components/ai-agent";
import Footer from "@/components/Footer";
// Lazy-loaded on demand: react-joyride is heavy and only needed on /dashboard
const ProductTour = lazy(() => import("@/components/ProductTour").then(m => ({ default: m.ProductTour })));
import { CrispChat } from "@/components/CrispChat";
import { PageContextProvider } from "@/hooks/use-page-context";
import { TipsPanel } from "@/components/tips";
import { cn } from "@/lib/utils";
import { SectionNavItem, NavItem } from "@/config/navigation";
// SECURITY: Import cleanup functions for user-specific storage
import { clearSpotterConversation } from "@/hooks/use-ai-agent";
import { clearTourStatus } from "@/hooks/use-product-tour";
import { clearInsightsMaxHours } from "@/pages/Insights";
import { clearPayPeriodSettings } from "@/pages/ClientDetail";
import { clearSidebarFavorites } from "@/hooks/use-sidebar-favorites";

interface LayoutProps {
  children: ReactNode;
}

/**
 * Layout component for trainer/admin pages.
 *
 * NOTE: Client detection/redirection is handled by TrainerGuard wrapper in App.tsx.
 * This component can assume the user is authorized as a trainer/admin.
 */
const Layout = ({ children }: LayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, session, isLoading: authLoading, signOut: authSignOut } = useAuth();
  const [initials, setInitials] = useState<string | null>(null);
  const [fullName, setFullName] = useState<string | null>(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [expandedMobileSections, setExpandedMobileSections] = useState<Set<string>>(() => {
    const saved = sessionStorage.getItem("mobile-expanded-sections");
    if (saved) {
      try {
        const parsed = new Set(JSON.parse(saved) as string[]);
        parsed.add(FAVORITES_SECTION_ID);
        return parsed;
      } catch { /* fall through */ }
    }
    return new Set([FAVORITES_SECTION_ID]);
  });
  const [profileLoading, setProfileLoading] = useState(true);
  const { organization, loading: orgLoading } = useOrganization();
  const { loading: featureFlagsLoading } = useFeatureFlags();
  const { sections, isFavorite, toggleFavorite } = useSidebarNavigation();

  // Initialize calendar sync (runs on login + every 10 mins)
  useCalendarSync();

  // Swipe gestures for mobile navigation
  const swipeState = useSwipe({
    onSwipeRight: () => setMobileMenuOpen(true),
    onSwipeLeft: () => setMobileMenuOpen(false),
    threshold: 60,
    edgeOnly: true,
    edgeWidth: 30,
  });

  // NOTE: TrainerGuard already handles auth-checking before Layout renders.
  // A redundant redirect here caused login-screen flashes during Google OAuth
  // because Layout could mount before the session context had propagated.

  // Function to load profile data
  const loadProfile = useCallback(() => {
    if (user?.id) {
      setProfileLoading(true);
      supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', user.id)
        .single()
        .then(({ data }) => {
          const firstName = data?.first_name || '';
          const lastName = data?.last_name || '';
          const firstInitial = firstName.charAt(0)?.toUpperCase() || '';
          const lastInitial = lastName.charAt(0)?.toUpperCase() || '';
          setInitials(firstInitial + lastInitial || user?.email?.charAt(0)?.toUpperCase() || '?');

          // Set full name for display
          const name = [firstName, lastName].filter(Boolean).join(' ');
          setFullName(name || null);
        })
        .finally(() => {
          setProfileLoading(false);
        });
    } else if (!authLoading) {
      // Only set loading false if we've confirmed there's no user
      setProfileLoading(false);
    }
  }, [user?.id, user?.email, authLoading]);

  // Fetch profile name and compute initials when user changes
  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleLogout = async () => {
    try {
      // SECURITY: Clear user-specific storage BEFORE signing out (while we still have user ID)
      if (user?.id) {
        try {
          clearSpotterConversation(user.id);
          clearTourStatus(user.id);
          clearInsightsMaxHours(user.id);
          clearPayPeriodSettings(user.id);
          clearSidebarFavorites(user.id);
        } catch (e) {
          console.warn('[Auth] Failed to clear user-specific storage:', e);
        }
      }

      // Centralized signout (local + token purge + best-effort global)
      await authSignOut();

      // Navigate to auth and hard reload to ensure a clean boot with no session
      navigate('/auth', { replace: true });
      setTimeout(() => window.location.reload(), 0);
    } catch (err) {
      console.warn('[Auth] Logout error:', err);
    }
  };

  // Mobile navigation helpers
  const isActive = (path: string) => {
    const [itemPath, itemQuery] = path.split("?");
    const currentPath = location.pathname;
    const currentSearch = location.search;

    if (currentPath === itemPath) {
      if (itemQuery) {
        return currentSearch === `?${itemQuery}`;
      }
      return true;
    }

    if (itemPath !== "/" && currentPath.startsWith(itemPath + "/")) {
      return true;
    }

    return false;
  };

  const hasActiveChild = (item: SectionNavItem) => {
    return item.children?.some((child) => isActive(child.path)) ?? false;
  };

  // Persist mobile expanded sections to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(
      "mobile-expanded-sections",
      JSON.stringify([...expandedMobileSections])
    );
  }, [expandedMobileSections]);

  // Auto-expand mobile section containing the active route (accordion: only one non-Favorites open)
  useEffect(() => {
    for (const section of sections) {
      if (section.id === FAVORITES_SECTION_ID) continue;
      const sectionHasActiveItem = section.items.some(
        (item) => isActive(item.path) || hasActiveChild(item)
      );
      if (sectionHasActiveItem && !expandedMobileSections.has(section.id)) {
        setExpandedMobileSections(new Set([FAVORITES_SECTION_ID, section.id]));
        break;
      }
    }
  }, [location.pathname, location.search, sections]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const toggleMobileSection = (sectionId: string) => {
    if (sectionId === FAVORITES_SECTION_ID) return; // Can't collapse Favorites
    setExpandedMobileSections((prev) => {
      const next = new Set([FAVORITES_SECTION_ID]);
      if (!prev.has(sectionId)) next.add(sectionId);
      return next;
    });
  };

  const handleMobileItemClick = (item: SectionNavItem) => {
    if (item.children && item.children.length > 0) {
      toggleSection(item.id);
    } else {
      navigate(item.path);
      setMobileMenuOpen(false);
    }
  };

  const handleMobileChildClick = (child: NavItem) => {
    navigate(child.path);
    setMobileMenuOpen(false);
  };

  // TrainerGuard already handles auth checking before Layout is rendered,
  // so we shouldn't show a loading state here - just render children.

  return (
    <PageContextProvider>
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar - hidden on mobile, wait for profile to load */}
      <aside className="hidden md:block shrink-0 sticky top-0 h-screen">
        {profileLoading || featureFlagsLoading ? (
          <div className="flex flex-col h-full bg-background border-r border-border/40 w-64 animate-pulse">
            <div className="flex items-center h-16 px-4 border-b border-border/40 gap-2.5">
              <div className="h-8 w-8 rounded-lg bg-muted" />
              <div className="h-4 w-24 bg-muted rounded" />
            </div>
            <div className="flex-1 py-4 px-3 space-y-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-2">
                  <div className="h-3 w-16 bg-muted rounded mx-3" />
                  <div className="space-y-1">
                    {[1, 2, 3].map((j) => (
                      <div key={j} className="h-9 bg-muted rounded-lg mx-0" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <SidebarNavigation
            initials={initials}
            fullName={fullName}
            onLogout={handleLogout}
            onEditProfile={() => setProfileDialogOpen(true)}
          />
        )}
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-h-screen bg-background">
        {/* Mobile Header - only visible on mobile */}
        <header className="sticky top-0 z-10 border-b border-border/40 bg-background/80 backdrop-blur-md md:hidden">
          <div className="flex h-16 items-center justify-between px-4">
            {/* Mobile hamburger menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 bg-background border-r border-border/40">
                <div className="flex flex-col h-full">
                  {/* Mobile menu header */}
                  <div className="flex items-center gap-2.5 p-4 border-b border-border/40">
                    <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
                      <Activity className="h-4 w-4 text-primary-foreground" />
                    </div>
                    <span className="font-sans text-[15px] font-semibold text-foreground tracking-tight">
                      {organization?.name || "TrainerPulse"}
                    </span>
                  </div>

                  {/* Mobile navigation - grouped */}
                  <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
                    {sections.map((section) => {
                      const isMobileFavoritesSection = section.id === FAVORITES_SECTION_ID;
                      const isMobileSectionExpanded = expandedMobileSections.has(section.id);
                      return (
                      <div key={section.id}>
                        {isMobileFavoritesSection ? (
                          <div className="flex items-center justify-between w-full px-3 mb-2">
                            <h3 className="font-sans text-[11px] font-medium text-muted-foreground/70 capitalize">
                              {section.label.toLowerCase()}
                            </h3>
                          </div>
                        ) : (
                          <button
                            onClick={() => toggleMobileSection(section.id)}
                            className="flex items-center justify-between w-full px-3 mb-2"
                          >
                            <h3 className="font-sans text-[11px] font-medium text-muted-foreground/70 capitalize">
                              {section.label.toLowerCase()}
                            </h3>
                            <ChevronDown
                              className={cn(
                                "h-3.5 w-3.5 text-muted-foreground/50 transition-transform",
                                isMobileSectionExpanded && "rotate-180"
                              )}
                            />
                          </button>
                        )}
                        {isMobileSectionExpanded && (
                        <div className="space-y-1">
                          {section.items.map((item) => {
                            const hasChildren = item.children && item.children.length > 0;
                            const isExpanded = expandedSections.has(item.id);
                            const itemIsActive = isActive(item.path) || hasActiveChild(item);
                            const starred = isFavorite(item.id);
                            const isPermanent = !!item.permanent;
                            const showStarInFavorites = isMobileFavoritesSection && !isPermanent;
                            const showStarInOther = !isMobileFavoritesSection;

                            return (
                              <div key={`${section.id}-${item.id}`} className="group/item relative">
                                {itemIsActive && !hasChildren && (
                                  <span
                                    aria-hidden
                                    className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[2px] rounded-sm bg-primary"
                                  />
                                )}
                                <button
                                  onClick={() => handleMobileItemClick(item)}
                                  className={cn(
                                    "flex items-center gap-3 w-full pl-4 pr-3 py-2 rounded-lg transition-colors duration-200 text-[13px] font-medium tracking-tight",
                                    itemIsActive && !hasChildren
                                      ? "bg-primary/[0.06] text-foreground font-semibold"
                                      : itemIsActive && hasChildren
                                      ? "text-foreground bg-muted/50"
                                      : "text-muted-foreground hover:text-accent hover:bg-accent/[0.06]"
                                  )}
                                >
                                  <DynamicIcon
                                    name={item.icon}
                                    className={cn(
                                      "h-4 w-4 transition-colors duration-200",
                                      itemIsActive && !hasChildren && "text-primary"
                                    )}
                                  />
                                  <span className="flex-1 text-left">{item.label}</span>
                                  {hasChildren && (
                                    <ChevronDown
                                      className={cn(
                                        "h-4 w-4 transition-transform",
                                        isExpanded && "rotate-180"
                                      )}
                                    />
                                  )}
                                  {showStarInFavorites && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); toggleFavorite(item.id); }}
                                      className="p-0.5 rounded transition-colors shrink-0"
                                      title="Remove from favorites"
                                    >
                                      <Star className="h-3.5 w-3.5 fill-warning text-warning" />
                                    </button>
                                  )}
                                  {showStarInOther && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); toggleFavorite(item.id); }}
                                      className={cn(
                                        "p-0.5 rounded transition-all shrink-0",
                                        starred ? "opacity-100" : "opacity-0 group-hover/item:opacity-100"
                                      )}
                                      title={starred ? "Remove from favorites" : "Add to favorites"}
                                    >
                                      <Star
                                        className={cn(
                                          "h-3.5 w-3.5",
                                          starred
                                            ? "fill-warning text-warning"
                                            : "text-muted-foreground/50"
                                        )}
                                      />
                                    </button>
                                  )}
                                </button>

                                {hasChildren && isExpanded && (
                                  <div className="ml-5 mt-1 space-y-1 border-l border-border/60 pl-3">
                                    {item.children!.map((child) => {
                                      const childActive = isActive(child.path);
                                      return (
                                        <div key={child.id} className="relative">
                                          {childActive && (
                                            <span
                                              aria-hidden
                                              className="pointer-events-none absolute -left-[13px] top-1/2 -translate-y-1/2 h-4 w-[2px] rounded-sm bg-primary"
                                            />
                                          )}
                                          <button
                                            onClick={() => handleMobileChildClick(child)}
                                            className={cn(
                                              "flex items-center gap-3 w-full px-3 py-1.5 text-[13px] font-medium rounded-lg transition-colors duration-200 tracking-tight",
                                              childActive
                                                ? "bg-primary/[0.06] text-foreground font-semibold"
                                                : "text-muted-foreground hover:text-accent hover:bg-accent/[0.06]"
                                            )}
                                          >
                                            <DynamicIcon
                                              name={child.icon}
                                              className={cn(
                                                "h-4 w-4 transition-colors duration-200",
                                                childActive && "text-primary"
                                              )}
                                            />
                                            {child.label}
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        )}
                      </div>
                    );
                    })}
                  </nav>

                  {/* Mobile menu footer */}
                  <div className="p-3 border-t border-border/40 space-y-2">
                    <Button
                      variant={isActive("/settings") ? "secondary" : "ghost"}
                      className="w-full justify-start gap-3 text-[13px] font-medium"
                      onClick={() => { navigate("/settings"); setMobileMenuOpen(false); }}
                    >
                      <Settings className="h-4 w-4" />
                      My Settings
                    </Button>

                    {/* User info */}
                    <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-card/60 border border-border/40">
                      <Avatar className="h-8 w-8 shrink-0">
                        <AvatarFallback className="bg-primary text-primary-foreground font-sans font-semibold text-xs tracking-tight">
                          {initials || "?"}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <button
                          onClick={() => { setProfileDialogOpen(true); setMobileMenuOpen(false); }}
                          className="flex items-center gap-1 text-[13px] font-medium text-foreground hover:text-accent transition-colors group w-full text-left"
                        >
                          <span className="truncate">{fullName || "Set your name"}</span>
                          <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                        </button>
                      </div>
                    </div>

                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-3 text-[13px] font-medium text-destructive hover:bg-destructive/10"
                      onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    >
                      <LogOut className="h-4 w-4" />
                      Logout
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>

            {/* Logo - centered on mobile */}
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
                <Activity className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="font-sans text-[15px] font-semibold text-foreground tracking-tight">
                {organization?.name || "TrainerPulse"}
              </span>
            </div>

            {/* Mobile avatar */}
            {initials && (
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-primary text-primary-foreground font-sans font-semibold text-xs tracking-tight">
                  {initials}
                </AvatarFallback>
              </Avatar>
            )}
          </div>
        </header>

        {/* Swipe indicator - shows when swiping from left edge */}
        {swipeState.isSwiping && swipeState.direction === "right" && !mobileMenuOpen && (
          <div
            className="fixed inset-y-0 left-0 z-50 pointer-events-none md:hidden"
            style={{
              width: `${Math.max(4, swipeState.swipeProgress * 80)}px`,
              background: `linear-gradient(to right, hsl(var(--primary) / ${swipeState.swipeProgress * 0.4}), transparent)`,
              transition: swipeState.isSwiping ? 'none' : 'all 0.2s ease-out',
            }}
          >
            <div
              className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-16 rounded-full bg-primary/60"
              style={{
                opacity: swipeState.swipeProgress,
                transform: `translateY(-50%) scaleY(${0.5 + swipeState.swipeProgress * 0.5})`,
              }}
            />
          </div>
        )}

        {/* Main content */}
        <main
          key={location.key}
          className="container py-6 px-4 flex-1 animate-fade-in"
        >
          {children}
        </main>

        <Footer />
      </div>

      <ProfileEditDialog
        open={profileDialogOpen}
        onOpenChange={setProfileDialogOpen}
        onSuccess={loadProfile}
      />

      {/* AI Assistant Chat */}
      <AIAgentChat />

      {/* Tips Panel */}
      <TipsPanel />

      {/* Product Tour - lazy-loaded (react-joyride is ~60KB gzipped) */}
      <Suspense fallback={null}>
        <ProductTour autoStart />
      </Suspense>

      {/* Crisp Chat Support */}
      <CrispChat />

      {/* Global command palette is mounted at the app root (App.tsx) */}
    </div>
    </PageContextProvider>
  );
};

export default Layout;
